'use es6';

import { List, Map as ImmutableMap, OrderedMap } from 'immutable';
import { memo } from 'react';
import { PIVOT_TOO_LARGE } from 'reporting-data/constants/errorTypes';
import { Metrics } from '../../monitoring/Metrics';
import { PIVOT_SUCCESSFULLY_RENDERED, PIVOT_TOO_LARGE as PIVOT_TOO_LARGE_COUNTER } from '../../constants/trackingMetrics';
import { CURRENCY, DURATION, NUMBER, PERCENT } from 'reporting-data/constants/property-types';
import { getPropertyLabel, getReferenceLabel } from 'reporting-data/v2/dataset/datasetRecords';
import { foldTransform as fold, FOLD_KEY_FIELD, FOLD_VALUE_FIELD } from 'reporting-visualizations/transforms/fold';
import { DEFAULT_DEBUG } from '../../constants/debug';
import { CROSS_TAB } from '../../constants/visualizationTypes';
import { Directions, Order } from '../../field/order';
import { selectAndSortFieldValues } from '../../field/sort';
import { Render as RenderErrorMessage } from '../shared/ErrorMessage';
import { tableTooLarge } from '../shared/tableSizeLimit';
import { getPropertyFormatter, isFormattableProperty, NumberFormats } from '../../formatters/propertyFormatter';
import CrossTabRenderer from './components/CrossTabRenderer';
import { Alignments, CrossTabVisualization, Encoding, EncodingLabel, EncodingTypes } from './crossTabRecords';
import { getTotalMeasureAliasPivot, TOTAL_KEY_FIELD, SUBTOTAL_KEY_FIELD, GRAND_TOTAL_KEY_FIELD } from './dataHelpers/totals';
import I18n from 'I18n';
import { getFlattenedRowData } from './dataHelpers/flattenGroupedData';
import { hasReferences } from 'reporting-data/v2/dataset/datasetRecords';
import { jsx as _jsx } from "react/jsx-runtime";
export const type = CROSS_TAB;
export const record = CrossTabVisualization;
export const DISPLAY_MEASURE = 'DISPLAY_MEASURE';
export const SUBTOTAL_DISPLAY_HINT = `${SUBTOTAL_KEY_FIELD}size`;
const RIGHT_ALIGN_TYPES = [PERCENT, CURRENCY, NUMBER, DURATION];
const FOLD_FIELD_PROPERTY = ImmutableMap({
  name: FOLD_KEY_FIELD,
  type: 'string',
  label: 'Measures',
  references: ImmutableMap()
});
const getAlign = property => property && RIGHT_ALIGN_TYPES.includes(property.get('type')) ? Alignments.right : Alignments.left;
const recursivelyGroup = (groupingField, dataGroup, order) => {
  if (List.isList(dataGroup)) {
    const definedOrder = selectAndSortFieldValues({
      data: dataGroup
    }, groupingField, order);
    const groupedData = dataGroup.groupBy(row => row.get(groupingField));
    return groupedData.sortBy((_, key) => definedOrder.indexOf(key));
  }
  if (ImmutableMap.isMap(dataGroup)) {
    return dataGroup.map(value => recursivelyGroup(groupingField, value, order));
  }
  return dataGroup;
};
const getRecursiveGroups = (encodingList, crossTabDataset) => encodingList.reduce((data, encoding) => {
  const field = encoding.get('field');
  const order = encoding.get('order') || Order({
    direction: Directions.ascending
  });
  return recursivelyGroup(field, data, order);
}, crossTabDataset.get('data'));
const foldDataset = (dataset, fields) => {
  const foldedDataset = fold(dataset, {
    fold: fields,
    as: [FOLD_KEY_FIELD, FOLD_VALUE_FIELD]
  });
  const labeledDataset = foldedDataset.update('properties', (properties = ImmutableMap()) => {
    const toReference = field => ImmutableMap({
      id: field,
      label: properties.getIn([field, 'label'])
    });
    const references = fields.toMap().flip().map((_, field) => toReference(field));
    const property = FOLD_FIELD_PROPERTY.set('references', references);
    return properties.set(FOLD_KEY_FIELD, property);
  });
  return labeledDataset;
};
const setPropertyFormattingForDataset = (values, dataset) => {
  const updatedProperties = dataset.properties.map((value, key) => {
    const propertyEncoding = values.find(encoding => encoding.field === key);
    if (!propertyEncoding || !propertyEncoding.get('formattingObject') || value.get('type') !== NumberFormats.number && value.get('type') !== NumberFormats.currency) {
      return value;
    }
    return value.set('formattingType', propertyEncoding.get('formattingType')).set('formattingObject', propertyEncoding.get('formattingObject'));
  });
  return dataset.set('properties', updatedProperties);
};
const setPropertyFormattingForValues = visualization => {
  const {
    values,
    data,
    dataset
  } = visualization;
  const datasetWithFormatting = setPropertyFormattingForDataset(values, data.get(dataset));
  return visualization.setIn(['data', dataset], datasetWithFormatting);
};
const setPropertyFormattingForColumns = visualization => {
  const {
    columns,
    data,
    dataset
  } = visualization;
  const datasetWithFormatting = setPropertyFormattingForDataset(columns, data.get(dataset));
  return visualization.setIn(['data', dataset], datasetWithFormatting);
};
const handleMultipleDisplayValues = visualization => {
  const {
    values,
    rows,
    data,
    dataset,
    columns
  } = visualization;
  const isFolded = values.count() > 1 || columns.isEmpty() && rows.isEmpty();
  if (isFolded) {
    const updatedDataset = foldDataset(data.get(dataset), values.map(val => val.get('field')));
    const foldedColumnOrder = Order({
      direction: Directions.ascending,
      defined: values.map(encoding => encoding.get('field'))
    });
    const foldFieldEncoding = Encoding({
      field: FOLD_KEY_FIELD,
      order: foldedColumnOrder
    });
    const foldValueEncoding = Encoding({
      field: FOLD_VALUE_FIELD
    });
    return visualization.set('values', List.of(foldValueEncoding)).setIn(['data', dataset], updatedDataset).set('columns', columns.concat(List.of(foldFieldEncoding)));
  }
  return visualization;
};
const getFlatRowEncodings = rowEncodingsWithLabels => {
  const flatEncodings = rowEncodingsWithLabels.reduce((accum, encoding) => {
    return /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */(
      accum.concat(encoding.get('encodingLabels'))
    );
  }, List());
  const safeRowEncodings = rowEncodingsWithLabels.getIn([0, 'encodingLabels']) ? rowEncodingsWithLabels : List([ImmutableMap({
    encoding: Encoding({}),
    encodingLabels: List([])
  })]);
  return safeRowEncodings.setIn([0, 'encodingLabels'], flatEncodings).take(1);
};
const getDataColumnEncodings = (visualization, groupedColumnData, originalValues) => {
  visualization = setPropertyFormattingForColumns(visualization);
  const columns = visualization.getIn(['columns']);
  const showRowSubtotals = visualization.getIn(['showRowSubtotals']);
  const displayValue = visualization.getIn(['values', 0, 'field']);
  const fullDataset = visualization.getIn(['data', 'primary']);
  const displayProperty = fullDataset.getIn(['properties', displayValue]);
  const isFolded = displayValue === FOLD_VALUE_FIELD;
  let dataColumnModel = columns.map(encoding => ImmutableMap({
    encoding,
    encodingLabels: List()
  }));
  const pushToDataColumnModel = ({
    colSpan,
    columnIndex,
    headerData,
    label,
    align
  }) => {
    columnIndex = columnIndex || 0;
    const encodingLabels = dataColumnModel.getIn([columnIndex, 'encodingLabels']);
    const updatedEncodingLabels = encodingLabels.push(EncodingLabel({
      label,
      encodingSize: colSpan,
      headerData,
      align
    }));
    dataColumnModel = dataColumnModel.setIn([columnIndex, 'encodingLabels'], updatedEncodingLabels);
  };
  const getDataColumnModelInfoRecursive = (col, key, columnIndex, headerData = ImmutableMap({})) => {
    const field = columns.getIn([columnIndex, 'field']);
    const measureProperty = fullDataset.getIn(['properties', key]);
    const nextColumnIsMeasures = columns.getIn([columnIndex + 1, 'field']) === FOLD_KEY_FIELD;
    const displaySubtotals = showRowSubtotals && !nextColumnIsMeasures;
    const columnProperty = fullDataset.getIn(['properties', field]);
    headerData = headerData.set(field, key);
    let colSpan = 0;
    if (!List.isList(col)) {
      col.forEach((v, k) => colSpan = colSpan + getDataColumnModelInfoRecursive(v, k, columnIndex + 1, headerData));
      if (displaySubtotals) {
        let isSubtotalLabeled = true;
        const remainingColumns = columns.count() - (columnIndex + 1);
        const remainingDepthToSpan = isFolded ? remainingColumns - 1 : remainingColumns;

        // We only display the initial subtotal label and sometimes a measure label, but we iterate through and store some subtotal information in each child column encoding to ensure the table has the correct width
        for (let colIndexIterator = columnIndex + 1; colIndexIterator < columns.count(); colIndexIterator++) {
          const columnIsMeasures = columns.getIn([colIndexIterator, 'field']) === FOLD_KEY_FIELD;

          // When using multiple measures, the label may span multiple columns until the last column, where it will display a measure label spanning just one column
          const subtotalColSpan = isFolded && columnIsMeasures ? 1 : originalValues.count();
          let rowSpan = 0;
          if (isSubtotalLabeled) {
            // The subtotal label will span all the rows to where data starts or the measure has to be labeled for multiple measures
            rowSpan = remainingDepthToSpan;
          } else if (columnIsMeasures) {
            // If there are multiple measures, display a measure label in the last column
            rowSpan = 1;
          }
          isSubtotalLabeled = false;
          const displayHint = rowSpan ? ImmutableMap({
            [SUBTOTAL_DISPLAY_HINT]: rowSpan
          }) : ImmutableMap({});
          if (isFolded && subtotalColSpan > 1) {
            const subtotalHeaderData = headerData.set(SUBTOTAL_KEY_FIELD, field);
            pushToDataColumnModel({
              label: I18n.text('report.details.header.SUBTOTAL_WITH_DIMENSION', {
                dimension: getReferenceLabel(fullDataset, field, key)
              }),
              colSpan: subtotalColSpan,
              columnIndex: colIndexIterator,
              headerData: subtotalHeaderData.merge(displayHint)
            });
          } else {
            originalValues.forEach(originalValue => {
              const originalValueField = originalValue.get('field');
              const foldedDisplayProperty = fullDataset.getIn(['properties', originalValueField]);
              let subtotalHeaderData = headerData.set(SUBTOTAL_KEY_FIELD, field);
              subtotalHeaderData = isFolded ? subtotalHeaderData.set(FOLD_KEY_FIELD, originalValueField) : subtotalHeaderData;
              const label = isFolded ? getPropertyLabel(fullDataset, originalValueField) : I18n.text('report.details.header.SUBTOTAL_WITH_DIMENSION', {
                dimension: getReferenceLabel(fullDataset, field, key)
              });
              pushToDataColumnModel({
                colSpan: subtotalColSpan,
                columnIndex: colIndexIterator,
                label,
                headerData: subtotalHeaderData.merge(displayHint),
                align: getAlign(isFolded && foldedDisplayProperty ? foldedDisplayProperty : displayProperty)
              });
            });
          }
        }
      }
    }
    let columnCount = displaySubtotals ? originalValues.count() : 0;
    columnCount = List.isList(col) ? 1 : columnCount;
    colSpan = colSpan + columnCount;
    const propertyUsesReferences = hasReferences(fullDataset, field, key);
    const isColumnPropertyFormattable = isFormattableProperty(columnProperty) && columnProperty.get('formattingObject');
    const columnLabel = propertyUsesReferences || !isColumnPropertyFormattable ? getReferenceLabel(fullDataset, field, key) : getPropertyFormatter(columnProperty)(key);
    pushToDataColumnModel({
      label: columnLabel,
      headerData,
      columnIndex,
      colSpan,
      align: getAlign(isFolded && measureProperty ? measureProperty : displayProperty)
    });
    return colSpan;
  };
  groupedColumnData.forEach((initialColumn, initialKey) => getDataColumnModelInfoRecursive(initialColumn, initialKey, 0));
  return dataColumnModel;
};
const getColumnEncodingsWithDataColumnLabel = (columnEncodingsWithLabels, flatRowEncodings, crossTabDataset) => {
  const dataColumnWidth = columnEncodingsWithLabels.getIn([0, 'encodingLabels']).reduce((accum, encodingLabel) => accum + encodingLabel.get('encodingSize'), 0);
  const dataColumnLabel = columnEncodingsWithLabels.reduce((label, encoding) => {
    const encodingField = encoding.getIn(['encoding', 'field']);
    const propertyLabel = getPropertyLabel(crossTabDataset, encodingField);
    if (columnEncodingsWithLabels.first() === encoding) {
      return propertyLabel;
    }
    return encodingField === FOLD_KEY_FIELD ? label : /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
    label.concat(` / ${propertyLabel}`);
  }, '');
  const rowLabelsWithColHeader = flatRowEncodings.updateIn([0, 'encodingLabels'], encodingLabels => encodingLabels.push(EncodingLabel({
    label: dataColumnLabel,
    encodingSize: dataColumnWidth
  })));
  return rowLabelsWithColHeader.concat(columnEncodingsWithLabels);
};
const getColumnEncodingsWithRowGrandTotals = (columnEncodingsWithLabels, visualization, displayProperty, originalValues) => {
  const {
    rows,
    rowTotal,
    showRowTotal,
    values
  } = visualization;
  const fullDataset = visualization.getIn(['data', 'primary']);
  const displayValue = values.getIn([0, 'field']) || '';
  const rowGrandTotal = rowTotal.first();
  if (!rowGrandTotal && !showRowTotal) {
    return columnEncodingsWithLabels;
  }
  const totalMeasureAlias = getTotalMeasureAliasPivot(displayValue, rows.last() ? rows.last().get('field') : undefined);
  const rowGrandTotalEncodingLabel = EncodingLabel({
    encodingSize: showRowTotal ? originalValues.count() : 1,
    label: I18n.text('report.details.header.TOTAL'),
    headerData: OrderedMap({
      [TOTAL_KEY_FIELD]: totalMeasureAlias,
      [GRAND_TOTAL_KEY_FIELD]: getTotalMeasureAliasPivot(displayValue),
      rowGrandTotal
    }),
    align: getAlign(displayProperty)
  });
  const totalEncodingLabels = originalValues.map(value => {
    const field = value.get('field');
    const property = fullDataset.getIn(['properties', field]);
    return EncodingLabel({
      label: getPropertyLabel(fullDataset, field),
      encodingSize: 1,
      displayField: field,
      headerData: OrderedMap({
        [TOTAL_KEY_FIELD]: rows.last() ? rows.last().get('field') : undefined,
        DISPLAY_MEASURE: field,
        [GRAND_TOTAL_KEY_FIELD]: getTotalMeasureAliasPivot(field),
        rowGrandTotal
      }),
      align: getAlign(property)
    });
  });
  return columnEncodingsWithLabels.map(columnEncoding => {
    if (columnEncoding === columnEncodingsWithLabels.first()) {
      return columnEncoding.update('encodingLabels', encodingLabels => encodingLabels.push(rowGrandTotalEncodingLabel));
    }
    if (columnEncoding === columnEncodingsWithLabels.last()) {
      return columnEncoding.update('encodingLabels', encodingLabels => showRowTotal ? encodingLabels.concat(totalEncodingLabels) : encodingLabels.push(rowGrandTotalEncodingLabel));
    }
    return columnEncoding;
  });
};
const mapVisualization = visualization => {
  const {
    values: originalValues
  } = visualization;
  visualization = handleMultipleDisplayValues(setPropertyFormattingForValues(visualization));
  const {
    rows,
    values,
    rowTotal,
    columns,
    data,
    dataset
  } = visualization;
  const crossTabDataset = data.get(dataset);
  const groupedRowData = rows.length ? getRecursiveGroups(rows, crossTabDataset) : OrderedMap({
    value: crossTabDataset.get('data')
  });
  const groupedColumnData = getRecursiveGroups(columns, crossTabDataset);
  const displayValue = values.getIn([0, 'field']) || '';
  const displayProperty = crossTabDataset.getIn(['properties', displayValue]);
  const emptyColumnEncodingLabels = List([ImmutableMap({
    encoding: Encoding({
      field: displayValue
    }),
    encodingLabels: List([EncodingLabel({
      label: getPropertyLabel(crossTabDataset, displayValue),
      encodingSize: 1,
      headerData: OrderedMap({
        [displayValue]: displayValue
      }),
      align: getAlign(displayProperty)
    })])
  })]);
  const rowEncodingsWithLabels = rows.map(rowEncoding => {
    const propertyType = crossTabDataset.getIn(['properties', rowEncoding.get('field')]);
    return ImmutableMap({
      encoding: rowEncoding,
      encodingLabels: List([EncodingLabel({
        label: getPropertyLabel(crossTabDataset, rowEncoding.get('field')),
        headerData: OrderedMap({
          rowTotal
        }),
        align: getAlign(propertyType),
        encodingType: EncodingTypes.row
      })])
    });
  });
  const flatRowEncodings = getFlatRowEncodings(rowEncodingsWithLabels);
  let headerEncodingsWithLabels;
  if (columns.count() === 0) {
    headerEncodingsWithLabels = flatRowEncodings.updateIn([0, 'encodingLabels'], encodingLabels => encodingLabels.push(emptyColumnEncodingLabels.getIn([0, 'encodingLabels', 0])));
  } else {
    const dataColumnEncodings = getDataColumnEncodings(visualization, groupedColumnData, originalValues);
    headerEncodingsWithLabels = getColumnEncodingsWithDataColumnLabel(dataColumnEncodings, flatRowEncodings, crossTabDataset);
    headerEncodingsWithLabels = getColumnEncodingsWithRowGrandTotals(headerEncodingsWithLabels, visualization, displayProperty, originalValues);
  }
  const columnEncodingsWithLabels = headerEncodingsWithLabels.updateIn([headerEncodingsWithLabels.count() - 1, 'encodingLabels'], encodingLabels => encodingLabels.filter(encodingLabel => encodingLabel.get('encodingType') !== EncodingTypes.row));
  const showColumnSubtotals = visualization.getIn(['showColumnSubtotals']);
  const flattenedRowData = getFlattenedRowData(rows, groupedRowData, showColumnSubtotals, crossTabDataset);
  return {
    visualization,
    flattenedRowData,
    rowEncodingsWithLabels,
    headerEncodingsWithLabels,
    columnEncodingsWithLabels,
    valueEncodings: originalValues
  };
};
const CrossTabViz = ({
  visualization,
  debug = DEFAULT_DEBUG,
  onReportVisualize
}) => {
  const {
    visualization: updatedVisualization,
    flattenedRowData,
    rowEncodingsWithLabels,
    headerEncodingsWithLabels,
    columnEncodingsWithLabels,
    valueEncodings
  } = mapVisualization(visualization);
  const tableHeight = flattenedRowData.count();
  let tableWidth = columnEncodingsWithLabels.getIn([0, 'encodingLabels']).reduce((accum, encodingLabel) => accum + encodingLabel.get('encodingSize'), 0);
  if (tableWidth === 1) {
    // Case where there are no columns selected
    tableWidth = rowEncodingsWithLabels.count() + valueEncodings.count();
  }
  if (tableTooLarge(tableHeight, tableWidth)) {
    Metrics.counter(PIVOT_TOO_LARGE_COUNTER).increment();
    return /*#__PURE__*/_jsx(RenderErrorMessage, {
      type: PIVOT_TOO_LARGE
    });
  }
  Metrics.counter(PIVOT_SUCCESSFULLY_RENDERED).increment();
  return /*#__PURE__*/_jsx(CrossTabRenderer, {
    visualization: updatedVisualization,
    flattenedRowData: flattenedRowData,
    rowEncodingsWithLabels: rowEncodingsWithLabels,
    headerEncodingsWithLabels: headerEncodingsWithLabels,
    columnEncodingsWithLabels: columnEncodingsWithLabels,
    originalVisualization: visualization,
    debug: debug,
    onReportVisualize: onReportVisualize
  });
};
export const Render = /*#__PURE__*/memo(CrossTabViz);